import { useVirtualizer } from '@tanstack/react-virtual';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { type MouseEvent, memo, useCallback } from 'react';

import { useTrackSwitchedWorkspace } from '@air/analytics';
import { type Workspace } from '@air/api/types';
import { DropdownMenuRadioGroup, DropdownMenuRadioItem } from '@air/primitive-dropdown-menu';
import { type FullWorkspace } from '@air/types';

import { useWorkspacesSwitchWorkspacseMutation } from '~/components/v2/hooks/queries/mutations/useWorkspacesSwitchWorkspacseMutation';
import { useDevToolsStore } from '~/components/v2/store/features/devtools/useDevToolsStore';
import { Pathname } from '~/constants/pathname';
import { useCopyTextToClipboard } from '~/hooks/useCopyTextToClipboard';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useWorkspaces } from '~/swr-hooks/workspaces/useWorkspaces';

export type WorkspaceHeaderInformationDropdownSwitchWorkspaceItemsWorkspacesProps = {
  scrollElement: HTMLDivElement;
};

export const WorkspaceHeaderInformationDropdownSwitchWorkspaceItemsWorkspaces = memo(
  ({ scrollElement }: WorkspaceHeaderInformationDropdownSwitchWorkspaceItemsWorkspacesProps) => {
    const router = useRouter();
    const { data: workspaces = [] } = useWorkspaces();
    const { currentWorkspace, currentWorkspaceRef } = useCurrentWorkspace();
    const { mutate: switchWorkspaceMutation } = useWorkspacesSwitchWorkspacseMutation();
    const { copyTextToClipboard } = useCopyTextToClipboard();
    const workspaceIdVisible = useDevToolsStore((store) => store.workspaceIdVisible);
    const { trackSwitchedWorkspace } = useTrackSwitchedWorkspace();
    const virtualizer = useVirtualizer({
      count: workspaces.length,
      estimateSize: () => 32,
      getScrollElement: () => scrollElement,
      overscan: 10,
    });

    const onValueChange = useCallback(
      async (workspaceId: Workspace['id']) => {
        await router.push({
          pathname: Pathname.Search,
        });

        switchWorkspaceMutation(
          { workspaceId },
          {
            onSuccess: (_data, variables) => {
              trackSwitchedWorkspace({
                fromId: currentWorkspaceRef.current?.id,
                fromName: currentWorkspaceRef.current?.name,
                toId: variables.workspaceId,
              });
            },
          },
        );
      },
      [currentWorkspaceRef, router, switchWorkspaceMutation, trackSwitchedWorkspace],
    );

    const onCopyWorkspaceId = useCallback(
      ({ event, workspace }: { event: MouseEvent<HTMLDivElement>; workspace: FullWorkspace }) => {
        event.preventDefault();

        copyTextToClipboard({
          errorMessage: `Failed to copy Workspace ID for ${workspace.name}`,
          text: workspace.id,
          successMessage: `Workspace ID copied to clipboard for ${workspace.name}`,
        });
      },
      [copyTextToClipboard],
    );

    return (
      <DropdownMenuRadioGroup onValueChange={onValueChange} value={currentWorkspace?.id}>
        <div
          className="relative w-full"
          style={{
            height: `${virtualizer.getTotalSize()}px`,
          }}
        >
          {virtualizer.getVirtualItems().map((virtualItem) => {
            const workspace = workspaces[virtualItem.index];

            return (
              <div
                className="absolute top-0 left-0 w-full"
                data-index={virtualItem.index}
                key={virtualItem.key}
                style={{
                  transform: `translateY(${virtualItem.start}px)`,
                }}
                ref={virtualizer.measureElement}
              >
                <DropdownMenuRadioItem
                  data-testid="DROPDOWN_MENU_RADIO_ITEM"
                  data-title={workspace.name}
                  data-workspace-id={workspace.id}
                  description={workspaceIdVisible ? workspace.id : undefined}
                  label={workspace.name}
                  key={workspace.id}
                  onContextMenu={(event) => onCopyWorkspaceId({ event, workspace })}
                  prefix={
                    <div className="size-6 overflow-hidden rounded-4 border border-grey-4 bg-grey-1">
                      <Image
                        alt={workspace.name}
                        className="size-full"
                        height={24}
                        style={{ objectFit: 'cover' }}
                        src={workspace.image}
                        width={24}
                      />
                    </div>
                  }
                  size="small"
                  value={workspace.id}
                />
              </div>
            );
          })}
        </div>
      </DropdownMenuRadioGroup>
    );
  },
);

WorkspaceHeaderInformationDropdownSwitchWorkspaceItemsWorkspaces.displayName =
  'WorkspaceHeaderInformationDropdownSwitchWorkspaceItemsWorkspaces';
